import { Toaster } from '@talentmesh/ui/components/sonner';
import { cn } from '@talentmesh/ui/lib/utils';
import React from 'react';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import Footer from './Cards/Footer';
import Header from './Cards/Header';
import RecruitmentCard from './Cards/RecruitmentCard';
import RecruitmentCardMobile from './Cards/RecruitmentCardMobile';
import SkillsCard from './Cards/SkillsCard';
import CompanyDescription from './components/CompanyDescription';
import RichTextContent from './components/RichTextContent';
import { JobAd } from './Models/JobAd';
import { Settings } from './Models/Settings';
import { testToUIString } from './utils/UIStringsFormattings';

export interface JobAdPageProps {
    jobAd: JobAd;
    settings: Settings;
}

function getPaddingClass(preview: boolean): string {
    if (isMobile) {
        return 'pt-4';
    }
    if (preview) {
        return 'pt-0';
    }
    return 'pt-16';
}

export function JobAdPage({ jobAd, settings }: JobAdPageProps): React.JSX.Element {
    const {
        recruitmentId,
        companyIcon,
        companyName,
        companyDescription,
        jobAdTitle,
        jobAdDescription,
        tests,
        skills,
        jobDetails,
    } = jobAd;

    const { platformUrl, websiteUrl, preview } = settings;
    const onBoardUrl = `${platformUrl}/execute?assessmentId=${recruitmentId}`;

    return (
        <div className={cn('flex min-h-screen flex-col', getPaddingClass(preview))}>
            <div className={cn('flex flex-1 flex-col', isMobile && 'px-4')}>
                <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
                    <div className="hidden md:col-span-1 md:block" />
                    <main className="col-span-1 flex flex-col gap-4 md:col-span-7">
                        <Header
                            title={jobAdTitle}
                            companyName={companyName}
                            location={jobDetails.locationDetails.description}
                            workArrangement={jobDetails.workArrangement}
                            employmentType={jobDetails.employmentType}
                            salary={jobDetails.salary}
                            companyIconUrl={companyIcon}
                        />
                        {(isMobile || isTablet) && !preview && (
                            <RecruitmentCardMobile
                                companyName={companyName}
                                tests={tests.map((item) => testToUIString(item, jobDetails.jobFunctionName))}
                                jobAdTitle={jobAdTitle}
                                onBoardUrl={onBoardUrl}
                            />
                        )}
                        {skills.length > 0 && <SkillsCard skills={skills} />}
                        <RichTextContent html={jobAdDescription} />
                        <CompanyDescription companyDescription={companyDescription} />
                    </main>
                    {isDesktop && !preview && (
                        <aside className="col-span-1 md:col-span-3">
                            <RecruitmentCard
                                companyName={companyName}
                                tests={tests.map((item) => testToUIString(item, jobDetails.jobFunctionName))}
                                jobAdTitle={jobAdTitle}
                                onBoardUrl={onBoardUrl}
                            />
                        </aside>
                    )}
                    <div className="hidden md:col-span-1 md:block" />
                </div>
            </div>
            <div className="mt-auto">
                <Footer companyName={companyName} websiteUrl={websiteUrl} />
            </div>
            <Toaster position="bottom-left" />
        </div>
    );
}
